// src/modules/Clients/ClientRules/components/containers/Main.tsx
// Listado de reglas de un cliente: diseño de tablero, sin ruta de detalle. Crear/editar en panel lateral.
import { createHttpClient } from '@adapters/HttpClient';
import { FilterConditionOutput } from '@components/ui/AdvancedFilter';
import { Input } from '@components/ui/shadcn/input';
import { SortConfig } from '@components/ui/VirtualTable';
import { faFlask, faSearch } from '@fortawesome/free-solid-svg-icons';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { RulesApiAdapter } from '@modules/configuration/Rules/adapters/RulesApiAdapter';
import { CardList } from '@modules/configuration/Rules/components/display';
import { ErrorState, LoadingState } from '@modules/configuration/Rules/components/feedback';
import { DeleteModal, DeleteModalRef } from '@modules/configuration/Rules/components/modals';
import { useRules } from '@modules/configuration/Rules/hooks/useRules';
import { RulesModel } from '@modules/configuration/Rules/models/Rules';
import { prefetchRulesReferenceData } from '@modules/configuration/Rules/services/prefetchRulesReferenceData';
import { Service } from '@modules/configuration/Rules/services/Service';
import { Button } from '@shared/components/ui/button';
import { EMPTY_VIEW_ICONS, EmptyView } from '@shared/components/ui/EmptyView';
import { useToast } from '@shared/hooks/use-toast';
import { usePermissions } from '@shared/hooks/usePermissions';
import { useQueryParams } from '@shared/hooks/useQueryParams';
import { apiClientWH } from '@shared/services';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';
import { ClientRulesBreadcrumb } from '../Breadcrumb';
import { ClientRuleBoardCard } from '../ClientRuleBoardCard';
import { ClientRuleFormSheet } from '../ClientRuleFormSheet';
import { ShippingRulePreviewModal } from '../modals/ShippingRulePreviewModal';

type FormMode = 'create' | 'edit';

export const Main: React.FC = () => {
  const { t } = useTranslation('rulesModule');
  const { toast } = useToast();
  const { clientId } = useParams<{ clientId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { canAccess } = usePermissions();
  const teamId = clientId ? parseInt(clientId, 10) : 0;
  const deleteModalRef = useRef<DeleteModalRef>(null);
  const legacyQueryHandled = useRef(false);

  const canCreate = canAccess(['tenant', 'tenant_3pl'], ['shipping.rules.create']);
  const canEdit = canAccess(['tenant', 'tenant_3pl'], ['shipping.rules.edit']);
  const canDelete = canAccess(['tenant', 'tenant_3pl'], ['shipping.rules.edit']);

  const [refDataEpoch, setRefDataEpoch] = useState(0);
  /** Se incrementa al guardar el orden para remontar `CardList` y evitar estado obsoleto. */
  const [rulesListEpoch, setRulesListEpoch] = useState(0);
  useEffect(() => {
    void prefetchRulesReferenceData().finally(() => {
      setRefDataEpoch((e) => e + 1);
    });
  }, []);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<FormMode>('create');
  const [editingRuleId, setEditingRuleId] = useState<number | null>(null);
  const [formSession, setFormSession] = useState(0);

  const httpClient = useMemo(() => createHttpClient(apiClientWH), []);
  const service = useMemo(() => new Service(new RulesApiAdapter(httpClient)), [httpClient]);

  const { params: queryParams, updateParams: updateQueryParams } = useQueryParams({
    filters: { type: 'json', default: [] as FilterConditionOutput[] },
    sortBy: { type: 'string', default: 'priority' },
    sortDirection: { type: 'string', default: 'asc' },
  });

  const [filterConditions, setFilterConditions] = useState<FilterConditionOutput[]>(
    Array.isArray(queryParams.filters) ? queryParams.filters : [],
  );
  const [sorting, setSorting] = useState<SortConfig | null>(() => ({
    field: queryParams.sortBy || 'priority',
    direction: (queryParams.sortDirection as 'asc' | 'desc') || 'asc',
  }));

  useEffect(() => {
    setFilterConditions(Array.isArray(queryParams.filters) ? queryParams.filters : []);
  }, [queryParams.filters]);

  useEffect(() => {
    const nextSorting = {
      field: queryParams.sortBy || 'priority',
      direction: (queryParams.sortDirection as 'asc' | 'desc') || 'asc',
    };
    setSorting((prev) => {
      if (prev && prev.field === nextSorting.field && prev.direction === nextSorting.direction) {
        return prev;
      }
      return nextSorting;
    });
  }, [queryParams.sortBy, queryParams.sortDirection]);

  const processedFilters = useMemo(() => {
    const event_ids: number[] = [];
    filterConditions.forEach((cond) => {
      if (cond.column === 'event' && cond.action === 'equals') {
        event_ids.push(Number(cond.value));
      }
    });
    return {
      ...(event_ids.length > 0 && { event_ids }),
      sortBy: sorting?.field ?? 'priority',
      sortDirection: sorting?.direction ?? 'asc',
      team_id: teamId,
    };
  }, [filterConditions, sorting, teamId]);

  const [searchInput, setSearchInput] = useState('');
  const searchTrimmed = searchInput.trim();

  const { rules, isLoading, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage, total } = useRules({
    searchTerm: '',
    ...processedFilters,
  });

  const filteredRules = useMemo(() => {
    if (!searchTrimmed) return rules;
    const q = searchTrimmed.toLowerCase();
    return rules.filter((r) => r.name.toLowerCase().includes(q));
  }, [rules, searchTrimmed]);

  /** Regla del listado para abrir el editor sin GET /shipping-rules/:id */
  const initialRuleForEdit = useMemo(() => {
    if (formMode !== 'edit' || editingRuleId == null) return undefined;
    return rules.find((r) => r.id === editingRuleId);
  }, [formMode, editingRuleId, rules]);

  const handleReorderSaveSuccess = useCallback(() => {
    setRulesListEpoch((e) => e + 1);
    void refetch();
  }, [refetch]);

  const openCreate = useCallback(() => {
    setFormSession((s) => s + 1);
    setFormMode('create');
    setEditingRuleId(null);
    setFormOpen(true);
  }, []);

  const openEdit = useCallback((id: number) => {
    setFormSession((s) => s + 1);
    setFormMode('edit');
    setEditingRuleId(id);
    setFormOpen(true);
  }, []);

  const closeForm = useCallback(() => {
    setFormOpen(false);
    setEditingRuleId(null);
    void refetch();
  }, [refetch]);

  useEffect(() => {
    if (legacyQueryHandled.current) return;
    const sheet = searchParams.get('sheet');
    const eid = searchParams.get('editRule');
    if (sheet === 'create' && canCreate) {
      legacyQueryHandled.current = true;
      openCreate();
      setSearchParams({}, { replace: true });
    } else if (sheet === 'edit' && eid && canEdit) {
      legacyQueryHandled.current = true;
      const n = Number(eid);
      if (!Number.isNaN(n)) {
        setFormSession((s) => s + 1);
        setFormMode('edit');
        setEditingRuleId(n);
        setFormOpen(true);
      }
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, canCreate, canEdit, openCreate, setSearchParams]);

  const handleSortingChange = useCallback(
    (newSorting: SortConfig | null) => {
      setSorting(newSorting);
      const sortBy = newSorting?.field || 'priority';
      const sortDirection = newSorting?.direction || 'asc';
      if (queryParams.sortBy !== sortBy || queryParams.sortDirection !== sortDirection) {
        updateQueryParams({
          sortBy: sortBy === 'priority' ? undefined : sortBy,
          sortDirection: sortDirection === 'asc' ? undefined : sortDirection,
        });
      }
    },
    [updateQueryParams, queryParams.sortBy, queryParams.sortDirection],
  );

  const handleToggleRule = useCallback(
    async (rule: RulesModel, activate: boolean) => {
      try {
        await service.toggleRule(rule.id, activate);
        toast({
          title: activate ? t('toast.activated') : t('toast.deactivated'),
          variant: 'default',
        });
        refetch();
      } catch (err: any) {
        console.error(err);
        toast({
          title: t('toast.errorToggle'),
          description: err?.message ?? String(err),
          variant: 'destructive',
        });
      }
    },
    [service, refetch, t, toast],
  );

  const handleDeleteRule = useCallback(
    async (rule: RulesModel) => {
      try {
        await service.deleteRule(rule.id);
        toast({ title: t('toast.deleted'), variant: 'default' });
        refetch();
      } catch (err: any) {
        console.error(err);
        toast({
          title: t('toast.errorDelete'),
          description: err?.message ?? String(err),
          variant: 'destructive',
        });
      }
    },
    [service, refetch, t, toast],
  );

  const confirmDeleteRule = useCallback(
    (rule: RulesModel) => {
      deleteModalRef.current?.open(() => void handleDeleteRule(rule), rule.name);
    },
    [handleDeleteRule],
  );

  const handleReorder = useCallback(() => {}, []);

  const handleEditRule = useCallback(
    (rule: RulesModel) => {
      if (canEdit) {
        openEdit(rule.id);
      }
    },
    [canEdit, openEdit],
  );

  const listTotal = searchTrimmed ? filteredRules.length : total;

  const rulesListProps = useMemo(
    () => ({
      rules: filteredRules,
      fetchNextPage,
      hasNextPage,
      isFetchingNextPage,
      sorting,
      onSortingChange: handleSortingChange,
      total: listTotal,
      onToggleRule: handleToggleRule,
      onDeleteRule: confirmDeleteRule,
      onEditRule: handleEditRule,
      onReorder: handleReorder,
      reorderTeamId: teamId,
      isReadOnly: !canEdit,
      canEdit,
      canDelete,
      CardComponent: ClientRuleBoardCard,
      className: 'border-0 bg-transparent shadow-none',
      estimateItemHeight: 360,
      fillContainer: true,
      reorderMode: 'onSave' as const,
      reorderDisabled: searchTrimmed.length > 0,
      onReorderSaveSuccess: handleReorderSaveSuccess,
    }),
    [
      filteredRules,
      fetchNextPage,
      hasNextPage,
      isFetchingNextPage,
      sorting,
      handleSortingChange,
      listTotal,
      handleToggleRule,
      confirmDeleteRule,
      handleEditRule,
      handleReorder,
      teamId,
      canEdit,
      canDelete,
      searchTrimmed,
      handleReorderSaveSuccess,
    ],
  );

  if (error) {
    return (
      <div className="px-6 pt-4">
        <ErrorState error={error} onRetry={refetch} />
      </div>
    );
  }

  const showSearchBar = !isLoading && (searchTrimmed.length > 0 || rules.length > 0);

  const previewButton = canEdit && (
    <Button type="button" variant="outline" size="sm" className="gap-2" onClick={() => setPreviewOpen(true)}>
      <FontAwesomeIcon icon={faFlask} className="h-4 w-4" />
      {t('clientList.preview.openButton')}
    </Button>
  );

  const addRuleButton = canCreate && (
    <Button type="button" onClick={openCreate} variant="primary" size="sm" className="gap-2">
      <FontAwesomeIcon icon={faPlus} className="h-4 w-4" />
      {t('modal.buttons.create')}
    </Button>
  );

  return (
    <div className="flex h-[calc(100dvh-3.5rem)] w-full min-h-0 flex-col px-6 pt-4 pb-5">
      <ClientRulesBreadcrumb />

      {showSearchBar && (
        <div className="mb-6 shrink-0 space-y-1.5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="relative min-w-0 max-w-md flex-1">
              <FontAwesomeIcon
                icon={faSearch}
                className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              />
              <Input
                type="search"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder={t('clientList.search_placeholder')}
                className="pl-10 transition-colors"
                aria-label={t('clientList.search_placeholder')}
              />
            </div>
            {(previewButton || addRuleButton) && (
              <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
                {previewButton}
                {addRuleButton}
              </div>
            )}
          </div>
          {searchTrimmed.length > 0 && canEdit && (
            <p className="text-xs text-muted-foreground">{t('clientList.reorder_disabled_hint')}</p>
          )}
        </div>
      )}

      {isLoading && <LoadingState message={t('loading.message')} />}

      {!isLoading && !searchTrimmed && rules.length === 0 && (
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center rounded-lg border-2 border-dashed border-border bg-muted/30 px-4 py-12">
          <p className="mb-4 max-w-md text-center text-sm text-muted-foreground">{t('clientList.empty_dashed')}</p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            {previewButton}
            {addRuleButton}
          </div>
        </div>
      )}

      {!isLoading && searchTrimmed && rules.length > 0 && filteredRules.length === 0 && (
        <EmptyView type="noResults" icon={EMPTY_VIEW_ICONS.DEFAULT} title={t('empty.notFound')} />
      )}

      {!isLoading && filteredRules.length > 0 && (
        <div
          key={`${refDataEpoch}-${rulesListEpoch}`}
          className="flex h-[min(100dvh-16rem,720px)] min-h-[320px] w-full min-w-0 flex-1 flex-col"
        >
          <CardList {...rulesListProps} showActions={canEdit || canDelete} />
        </div>
      )}

      {formOpen && teamId > 0 && (
        <ClientRuleFormSheet
          key={formSession}
          clientTeamId={teamId}
          mode={formMode}
          ruleId={formMode === 'edit' ? editingRuleId : null}
          initialRule={initialRuleForEdit}
          onClose={closeForm}
        />
      )}

      <DeleteModal ref={deleteModalRef} />

      <ShippingRulePreviewModal open={previewOpen} onOpenChange={setPreviewOpen} teamId={teamId} />
    </div>
  );
};
